import { useState } from 'react'

import {
  CustomFieldPrivacyOptions,
  CustomFieldSchema,
  CustomFieldType,
  CustomFieldValidators,
  MutationAddMemberSchemaFieldArgs,
  MutationAddPostTypeFieldArgs,
  MutationUnArchiveMemberSchemaFieldArgs,
  MutationUnarchivePostTypeFieldArgs,
  MutationUpdateMemberSchemaFieldArgs,
  MutationUpdatePostTypeFieldArgs,
  Network,
  PostType,
} from '@tribeplatform/gql-client/types'
import { UseMutateAsyncFunction } from '@tribeplatform/react-sdk/lib'
import { Accordion } from '@tribeplatform/react-ui-kit/Accordion'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { Icon } from '@tribeplatform/react-ui-kit/Icon'
import { randomColor } from '@tribeplatform/react-ui-kit/LabelPicker'
import { Modal } from '@tribeplatform/react-ui-kit/Modal'
import { MultiselectCreatable } from '@tribeplatform/react-ui-kit/Multiselect'

import { logger } from '../common/lib/logger.js'
import {
  CustomFieldSubtype,
  CustomFieldViews,
  getFieldIcon,
  getFieldSetting,
} from '../CustomField/utils.js'
import { FormLabelPicker } from '../Form/FormLabelPicker/FormLabelPicker.js'
import { Form } from '../Form/index.js'
import { T } from '../i18n/components/T.js'
import { useStaticIntl } from '../i18n/hooks/useStaticIntl.js'
import { useI18n } from '../i18n/providers/I18nProvider.js'
import type { I18nShape } from '../i18n/types.js'

import { FieldTypePicker } from './FieldTypePicker.js'
import {
  FieldTypePickerOption,
  fieldTypesFactory,
  getFieldTypeName,
  getFieldViewTranslatedName,
  supportedFieldViewsMapping,
  supportedInputViewsMappingFactory,
} from './utils.js'

const getFieldViewSettings = ({
  field,
  view,
  intl,
}: {
  field: Partial<CustomFieldSchema>
  view: CustomFieldViews
  intl: I18nShape
}) => {
  const { $t } = intl
  if (
    view === CustomFieldViews.Pill &&
    field?.type === CustomFieldType.boolean
  ) {
    return (
      <>
        <Form.Input
          name="settings.viewProps.trueText"
          label={$t({
            defaultMessage: 'Positive text',
            id: 'Admin.FieldModal.ViewSetting.Boolean.True',
          })}
        />
        <Form.Input
          name="settings.viewProps.falseText"
          label={$t({
            defaultMessage: 'Negative text',
            id: 'Admin.FieldModal.ViewSetting.Boolean.False',
          })}
        />
      </>
    )
  }

  if (view === CustomFieldViews.Button) {
    return (
      <Form.Input
        name="settings.viewProps.text"
        label={$t({
          defaultMessage: 'Button text',
          id: 'Generics.ButtonText',
        })}
        validation={{
          required: $t({
            defaultMessage: 'Please enter a text for the button',
            id: 'Admin.FieldModal.RequiredFields.ButtonText',
          }),
        }}
      />
    )
  }

  if (field?.type === CustomFieldType.date) {
    return (
      <Form.Select
        name="settings.viewProps.format"
        label={$t({
          defaultMessage: 'Date format',
          id: 'Generics.DateFormat',
        })}
        items={[
          {
            text: $t({
              defaultMessage: 'Locale date',
              id: 'Generics.LocaleDate',
            }),
            value: undefined,
          },
          {
            text: $t({
              defaultMessage: 'Human readable',
              id: 'Generics.HumanReadable',
            }),
            value: 'standard',
          },
          {
            text: $t({
              defaultMessage: 'Relative date',
              id: 'Generics.RelativeDate',
            }),
            value: 'relative',
          },
        ]}
      />
    )
  }

  return null
}

export const getFieldSubtypeSettings = ({
  subtype,
  intl,
}: {
  subtype: CustomFieldSubtype
  intl: I18nShape
}) => {
  const { $t } = intl
  if (
    subtype === CustomFieldSubtype.MEMBER ||
    subtype === CustomFieldSubtype.MEMBERS
  ) {
    return (
      <Form.Select
        name="settings.inputProps.memberGroup"
        label={$t({
          defaultMessage: 'Member group',
          id: 'Generics.MemberGroup',
        })}
        items={[
          {
            text: $t({
              defaultMessage: 'All members',
              id: 'Generics.AllMembers',
            }),
            value: 'all',
          },
          {
            text: $t({
              defaultMessage: 'Staff members',
              id: 'Generics.StaffMembers',
            }),
            value: 'staff',
          },
          {
            text: $t({
              defaultMessage: 'Regular members',
              id: 'Generics.RegularMembers',
            }),
            value: 'member',
          },
        ]}
      />
    )
  }

  return null
}

export const FieldModal = ({
  field: initialField,
  open,
  onClose,
  onUpdateField,
  isUpdating,
  onAddField,
  isAdding,
  onUnArchiveField,
  isUnarchiving,
  targetId,
  targetType,
}: {
  field?: Partial<CustomFieldSchema>
  open: boolean
  onClose: () => void
  onUpdateField: UseMutateAsyncFunction<
    Network | PostType,
    Error,
    MutationUpdateMemberSchemaFieldArgs | MutationUpdatePostTypeFieldArgs,
    unknown
  >
  onAddField: UseMutateAsyncFunction<
    Network | PostType,
    Error,
    MutationAddMemberSchemaFieldArgs | MutationAddPostTypeFieldArgs,
    unknown
  >
  onUnArchiveField?: UseMutateAsyncFunction<
    Network | PostType,
    Error,
    MutationUnArchiveMemberSchemaFieldArgs | MutationUnarchivePostTypeFieldArgs,
    unknown
  >
  isUpdating: boolean
  isAdding: boolean
  isUnarchiving: boolean
  targetId?: string
  targetType: 'post' | 'member'
}) => {
  const intl = useI18n()
  const { $t } = intl
  const fieldTypes = useStaticIntl(fieldTypesFactory)
  const [field, setField] = useState<FieldTypePickerOption>(initialField)
  const isSocialLink = getFieldSetting(field, 'category') === 'socialLink'

  let settings = { subtype: undefined, view: undefined }
  if (field?.settings && field?.settings.length) {
    settings = field.settings.reduce(
      (result, cur) => {
        try {
          result[cur.key] = JSON.parse(cur.value)
        } catch (e) {
          logger.error('Error parsing field setting value', e.message)
        }
        return result
      },
      { subtype: undefined, view: undefined },
    )
  }

  const permission = { read: 'everyone', write: 'own' }
  if (field?.readPrivacy?.allow?.length) {
    permission.read =
      field?.readPrivacy?.allow?.indexOf(CustomFieldPrivacyOptions.OWN) !== -1
        ? 'own'
        : 'admin'
  }
  if (field?.writePrivacy?.allow) {
    permission.write =
      field?.writePrivacy?.allow?.indexOf(CustomFieldPrivacyOptions.OWN) !== -1
        ? 'own'
        : 'admin'
  }

  let enumValidator = field?.validators?.find(
    validator => validator.validation === CustomFieldValidators.enum,
  )
  if (!enumValidator) {
    enumValidator = field?.items?.validators?.find(
      validator => validator.validation === CustomFieldValidators.enum,
    )
  }
  let initialOptions = []

  const selectOptions = getFieldSetting(field, 'selectOptions')
  if (selectOptions) {
    initialOptions = selectOptions?.map(option => ({
      id: option?.value,
      name: option?.value,
      colorHex: option?.color,
    }))
  } else if (enumValidator) {
    const optionsEnum = JSON.parse(enumValidator.value) || []
    if (optionsEnum?.length) {
      initialOptions = optionsEnum.map((option, index) => ({
        id: option,
        name: option,
        colorHex: randomColor(index),
      }))
    }
  }
  const [options, setOptions] = useState(initialOptions)
  const [externalKeys, setExternalKeys] = useState(field?.externalKeys || [])

  const onSubmit = values => {
    const { key, type, name, permission, settings, required } = values

    const input: CustomFieldSchema = {
      key,
      name,
      type,
      settings: [],
      validators: [],
      required,
      externalKeys,
    }
    if (field?.items && !field?.key) {
      input.items = field?.items
    }

    if (field?.typeOptions && !field?.key) {
      input.typeOptions = field?.typeOptions
    }

    if (permission?.read) {
      let allow = []
      if (permission?.read === 'admin') {
        allow = [CustomFieldPrivacyOptions.ADMIN]
      } else if (permission?.read === 'own') {
        allow = [CustomFieldPrivacyOptions.OWN, CustomFieldPrivacyOptions.ADMIN]
      }

      input.readPrivacy = {
        allow,
      }
    }

    if (permission?.write) {
      input.writePrivacy = {
        allow:
          permission.write === 'admin'
            ? [CustomFieldPrivacyOptions.ADMIN]
            : [CustomFieldPrivacyOptions.OWN, CustomFieldPrivacyOptions.ADMIN],
      }
    }

    if (options) {
      const optionValues = options?.map(option => option.name)
      settings.selectOptions = options?.map(option => ({
        value: option?.name,
        color: option?.colorHex,
      }))

      if (settings.subtype === CustomFieldSubtype.SELECT) {
        const enumValidator = field?.validators?.find(
          validator => validator.validation === CustomFieldValidators.enum,
        )
        if (enumValidator) {
          enumValidator.value = JSON.stringify(optionValues)
        } else {
          if (!field.validators) {
            field.validators = []
          }
          field.validators.push({
            validation: CustomFieldValidators.enum,
            value: JSON.stringify(optionValues),
          })
        }
      } else if (settings.subtype === CustomFieldSubtype.MULTISELECT) {
        field.validators = []
        const enumValidator = field?.items?.validators?.find(
          validator => validator.validation === CustomFieldValidators.enum,
        )
        if (enumValidator) {
          enumValidator.value = JSON.stringify(optionValues)
        } else {
          if (!field?.items) {
            field.items = {
              key: 'options',
              name: 'Options',
              type: CustomFieldType.text,
            }
          }

          if (!field?.items?.validators) {
            field.items.validators = []
          }
          field.items.validators.push({
            validation: CustomFieldValidators.enum,
            value: JSON.stringify(optionValues),
          })
        }
      }
    }

    if (settings) {
      Object.keys(settings).forEach(key => {
        if (settings[key] !== null && typeof settings[key] !== 'undefined') {
          input.settings.push({
            key,
            value: JSON.stringify(settings[key]),
          })
        }
      })
    }

    input.validators = field?.validators
    if (field?.items) {
      if (typeof field?.items?.required !== 'undefined') {
        delete field.items.required
      }
      if (typeof field?.items?.description !== 'undefined') {
        delete field.items.description
      }
      input.items = field?.items
    }

    if (field?.key) {
      delete input.type
      return onUpdateField({ id: targetId, input }).then(() => {
        onClose()
      })
    }
    return onAddField({
      id: targetId,
      input: { ...input, type: field.type },
    }).then(() => {
      onClose()
    })
  }

  const getModalTitle = field => {
    if (field?.key) {
      return $t(
        {
          defaultMessage: 'Update field ({field_key})',
          id: 'Admin.FieldModal.Title.Update',
        },
        {
          field_key: field.key,
        },
      )
    }
    if (field?.type) {
      return $t(
        {
          defaultMessage: 'Add {field_type} field',
          id: 'Admin.FieldModal.Title.AddOfType',
        },
        {
          field_type: getFieldTypeName(field, fieldTypes)?.toLowerCase(),
        },
      )
    }
    return $t({
      defaultMessage: 'Add field',
      id: 'Generics.AddField',
    })
  }

  const supportedInputViewsMapping = useStaticIntl(
    supportedInputViewsMappingFactory,
  )

  const supportedInputTypes = supportedInputViewsMapping[field?.type]

  const locked = getFieldSetting(field, 'locked')
  const supportedFieldViews =
    supportedFieldViewsMapping[settings.subtype] ||
    supportedFieldViewsMapping[field?.type]

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!field.type || !field.name) {
          return onClose()
        }
        if (
          // eslint-disable-next-line no-alert
          window.confirm(
            $t({
              defaultMessage: 'Are you sure you want to discard your changes?',
              id: 'Admin.FieldModal.DiscardWarning',
            }),
          )
        ) {
          onClose()
        }
      }}
    >
      <Modal.Header title={getModalTitle(field)} />
      <Modal.Content>
        {!field.type || field.children ? (
          <FieldTypePicker
            targetType={targetType}
            onChange={field => {
              const supportedInputTypes =
                supportedInputViewsMapping[field?.type]
              setField({
                name: '',
                key: field.key,
                type: field.type,
                validators:
                  field?.validators || supportedInputTypes?.[0]?.validators,
                settings: field?.settings || supportedInputTypes?.[0]?.settings,
                typeOptions: field?.typeOptions,
                items: field?.items,
              })
            }}
          />
        ) : (
          <Form
            onSubmit={onSubmit}
            defaultValues={{
              ...field,
              ...{
                settings,
                permission,
                options,
              },
            }}
            className="flex flex-col space-y-3"
          >
            {({ watch, setValue }) => {
              const selectedView = watch('settings.view')
              return (
                <>
                  <Form.Input
                    label={$t({
                      defaultMessage: 'Name',
                      id: 'Generics.Name.Noun',
                    })}
                    name="name"
                  />
                  {!field?.key && (
                    <Form.Input
                      label={$t({
                        defaultMessage: 'Key',
                        id: 'Admin.FieldModal.Input.FieldKey',
                      })}
                      name="key"
                      helperText={$t({
                        defaultMessage:
                          'Key is the unique identifier for this field and you will not be able to change it in the future',
                        id: 'Admin.FieldModal.Input.FieldKey.HelperText',
                      })}
                      validation={{
                        required: $t({
                          defaultMessage: "This field can't be empty",
                          id: 'Generics.Validation.RequiredField',
                        }),
                        pattern: {
                          value: /^[a-zA-Z_]+$/,
                          message: $t({
                            defaultMessage:
                              'Key can only include alphabets, and _',
                            id: 'Admin.FieldModal.Input.FieldKey.Validation.Pattern',
                          }),
                        },
                      }}
                    />
                  )}
                  {supportedInputTypes?.length > 1 && (
                    <Form.Select
                      label={$t({
                        defaultMessage: 'Form appearance',
                        id: 'Admin.FieldModal.Input.FormAppearance',
                      })}
                      name="settings.subtype"
                      disabled={locked}
                      onChange={subtype => {
                        const newType = supportedInputTypes.find(type =>
                          subtype
                            ? getFieldSetting(type, 'subtype') === subtype
                            : !getFieldSetting(type, 'subtype'),
                        )
                        const supportedFieldViews =
                          supportedFieldViewsMapping[subtype] ||
                          supportedFieldViewsMapping[newType?.type]

                        setValue('settings.view', supportedFieldViews?.[0])
                        if (newType) {
                          if (newType?.settings) {
                            field.settings = [...newType.settings]
                          }
                          // if (newType?.typeOptions)
                          //   field.typeOptions = { ...newType.typeOptions }
                          if (newType?.validators) {
                            field.validators = [...newType.validators]
                          }
                          setField({
                            ...field,
                          })
                        }
                      }}
                      items={supportedInputTypes.map(type => ({
                        text: (
                          <div className="flex space-s-2 items-center">
                            <Icon size="md">{getFieldIcon(type)}</Icon>
                            <span>{type.name}</span>
                          </div>
                        ),
                        value: getFieldSetting(type, 'subtype') || undefined,
                      }))}
                      helperText={
                        locked
                          ? $t({
                              defaultMessage:
                                'This field has a custom validation and its type cannot be changed',
                              id: 'Admin.FieldModal.Input.FormAppearance.Helper',
                            })
                          : ''
                      }
                    />
                  )}
                  {getFieldSubtypeSettings({
                    subtype: settings.subtype,
                    intl,
                  })}
                  {!isSocialLink && supportedFieldViews?.length > 0 && (
                    <Form.Select
                      label={$t({
                        defaultMessage: 'Display appearance',
                        id: 'Admin.FieldModal.Input.DisplayAppearance.Label',
                      })}
                      name="settings.view"
                      items={supportedFieldViews.map(view => ({
                        text: (
                          <div className="flex space-s-2 items-center">
                            <span>
                              {getFieldViewTranslatedName(intl, view)}
                            </span>
                          </div>
                        ),
                        value: view,
                      }))}
                    />
                  )}
                  {getFieldViewSettings({ field, view: selectedView, intl })}
                  {[
                    CustomFieldSubtype.SELECT,
                    CustomFieldSubtype.MULTISELECT,
                  ].indexOf(settings.subtype) !== -1 && (
                    <FormLabelPicker
                      name="options"
                      deleteConfirmation={{
                        title: $t({
                          defaultMessage:
                            'Are you sure you want to remove this option for all users?',
                          id: 'Admin.FieldModal.Settings.MultiSelectRemoveOptionConfirmation',
                        }),
                        proceedLabel: 'Remove',
                        danger: true,
                      }}
                      onChange={options => setOptions(options)}
                      label={$t({
                        defaultMessage: 'Options',
                        id: 'Generics.Options',
                      })}
                      buttonLabel={$t({
                        defaultMessage: 'Add',
                        id: 'Generics.Add',
                      })}
                      itemCancelLabel={$t({
                        defaultMessage: 'Cancel',
                        id: 'Generics.Cancel',
                      })}
                    />
                  )}
                  <Accordion
                    variant="plus"
                    iconAlign="trailing"
                    className="-mx-2"
                  >
                    <Accordion.Button>
                      <T
                        defaultMessage="Advanced options"
                        id="Generics.AdvancedOptions"
                      />
                    </Accordion.Button>
                    <Accordion.Panel className="space-y-3">
                      {field.type !== CustomFieldType.boolean &&
                        field.type !== CustomFieldType.date && (
                          <Form.Input
                            label={
                              <T
                                defaultMessage="Placeholder <small>(optional)</small>"
                                id="Admin.FieldModal.Settings.Placeholder"
                              />
                            }
                            name="settings.placeholder"
                          />
                        )}
                      <Form.Input
                        label={
                          <T
                            defaultMessage="Helper text <small>(optional)</small>"
                            id="Admin.FieldModal.Settings.Helper"
                          />
                        }
                        helperText={
                          <T
                            defaultMessage="A helper text explaining the field like this one"
                            id="Admin.FieldModal.Settings.Helper.Sample"
                          />
                        }
                        name="settings.helperText"
                      />
                      {targetType === 'post' &&
                        field.type !== CustomFieldType.boolean && (
                          <Form.Toggle
                            name="required"
                            label={$t({
                              defaultMessage: 'Required',
                              id: 'Generics.Required',
                            })}
                          />
                        )}
                      <Form.Select
                        label={
                          <div className="flex space-s-1">
                            <span>
                              <T
                                defaultMessage="Who can see this field?"
                                id="Admin.FieldModal.Settings.ReadPermission"
                              />
                            </span>
                          </div>
                        }
                        name="permission.read"
                        items={[
                          {
                            text: $t({
                              defaultMessage: 'Everyone',
                              id: 'Admin.PermissionGroup.All',
                            }),
                            value: 'everyone',
                          },
                          {
                            text: $t({
                              defaultMessage: 'Staff only',
                              id: 'Admin.PermissionGroup.StaffOnly',
                            }),
                            value: 'admin',
                          },
                          {
                            text:
                              targetType === 'member'
                                ? $t({
                                    defaultMessage: 'Profile owner and staff',
                                    id: 'Admin.PermissionGroup.OwnersAndStaff',
                                  })
                                : $t({
                                    defaultMessage: 'Author and staff',
                                    id: 'Admin.PermissionGroup.AuthorAndStaff',
                                  }),
                            value: 'own',
                          },
                        ]}
                      />
                      <Form.Select
                        label={
                          <div className="flex space-s-1">
                            <span>
                              <T
                                defaultMessage="Who can update this field?"
                                id="Admin.FieldModal.Settings.EditPermission"
                              />
                            </span>
                          </div>
                        }
                        name="permission.write"
                        items={[
                          {
                            text: $t({
                              defaultMessage: 'Staff only',
                              id: 'Admin.PermissionGroup.StaffOnly',
                            }),
                            value: 'admin',
                          },
                          {
                            text:
                              targetType === 'member'
                                ? $t({
                                    defaultMessage: 'Profile owner and staff',
                                    id: 'Admin.PermissionGroup.OwnersAndStaff',
                                  })
                                : $t({
                                    defaultMessage: 'Author and staff',
                                    id: 'Admin.PermissionGroup.AuthorAndStaff',
                                  }),
                            value: 'own',
                          },
                        ]}
                        helperText={
                          targetType === 'member' &&
                          'Fields always can get updated through single sign-on.'
                        }
                      />
                      {targetType === 'member' && (
                        <FormControl>
                          <FormControl.Label>
                            <div className="flex space-s-1">
                              <span>
                                <T
                                  defaultMessage="External keys"
                                  id="Admin.FieldModal.Settings.ExternalKeys"
                                />
                              </span>
                            </div>
                          </FormControl.Label>
                          <MultiselectCreatable
                            value={externalKeys}
                            options={[]}
                            onChange={setExternalKeys}
                            searchable
                            onCreateItem={inputValue => {
                              setExternalKeys([...externalKeys, inputValue])
                            }}
                          >
                            <MultiselectCreatable.Button
                              placeholder={$t({
                                defaultMessage: 'Add external keys...',
                                id: 'Admin.FieldModal.Settings.ExternalKeys.Placeholder',
                              })}
                              name="externalKey"
                            />
                            <MultiselectCreatable.Items>
                              {({ creating, inputValue }) => {
                                return (
                                  <>
                                    {creating && inputValue?.length > 0 && (
                                      <MultiselectCreatable.Item
                                        value={inputValue}
                                        index={0}
                                      >
                                        <span>
                                          <T
                                            defaultMessage="Create {key_name}"
                                            id="Admin.FieldModal.Settings.ExternalKeys.Create"
                                            values={{
                                              key_name: (
                                                <span className="font-bold">
                                                  {inputValue}
                                                </span>
                                              ),
                                            }}
                                          />
                                        </span>
                                      </MultiselectCreatable.Item>
                                    )}
                                  </>
                                )
                              }}
                            </MultiselectCreatable.Items>
                          </MultiselectCreatable>
                          <FormControl.Meta
                            helperText={$t({
                              defaultMessage:
                                'External keys are used to fill your custom profile field using SSO. The first key with a non-Empty value will be used.',
                              id: 'Admin.FieldModal.Settings.ExternalKeys.Helper',
                            })}
                          />
                        </FormControl>
                      )}
                    </Accordion.Panel>
                  </Accordion>
                  <Form.Actions className="pt-3">
                    <Button
                      loading={isAdding || isUpdating}
                      size="lg"
                      type="submit"
                    >
                      {field?.key ? (
                        <T defaultMessage="Update" id="Generics.Update" />
                      ) : (
                        <T defaultMessage="Create" id="Generics.Create" />
                      )}
                    </Button>
                    {field.archived && (
                      <Button
                        variant="secondaryNeutral"
                        loading={isUnarchiving}
                        onClick={async () => {
                          await onUnArchiveField({
                            key: field.key,
                            id: targetId,
                          })
                          onClose()
                        }}
                        size="lg"
                      >
                        <T defaultMessage="Unarchive" id="Generics.Unarchive" />
                      </Button>
                    )}
                  </Form.Actions>
                </>
              )
            }}
          </Form>
        )}
      </Modal.Content>
    </Modal>
  )
}
